import { AddCircleOutline, Close, KeyboardArrowDown } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { format as dfFormat, isValid as isValidDate } from "date-fns";
import { useEffect, useMemo, useState, useCallback } from "react";

import useService from "../../../../../../../hooks/useService";
import Svg from "../../../../../../ui library/components/svgs/standard/svg";
import {
  formatDate,
  formatIsoOrTime,
  getCenturyDateBounds,
  isWithinDateBounds,
  toValidDate,
} from "../../../../../../ui library/helpers/functions";
import HNSColors from "../../../../../../ui library/HNSColors";
import QuestionTitle from "../../../components/QuestionTitle";
import { handleAddRow } from "../helpers/functions";
import sharedStyles from "../styles/formStyles";

const CustomArrowIcon = (props) => (
  <KeyboardArrowDown
    {...props}
    sx={{ fontSize: 24, color: `${HNSColors.primary[500]} !important` }}
  />
);

const parseTimeStringToDate = (timeStr) => {
  if (!timeStr) {
    return null;
  }
  const [h = "00", m = "00", s = "00"] = timeStr.split(":");
  const d = new Date();
  d.setHours(Number(h) || 0, Number(m) || 0, Number(s) || 0, 0);
  return d;
};

const toHHmmss = (date) => {
  if (!date) {
    return "";
  }
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const ProviderTypeCell = ({ value, rowIdx, dependantValue, isEditable, handleChange, field }) => {
  const enumRepository = useService("repositories/enum-repository");
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const fetchOptions = async () => {
      const enumUrl = field.enum;
      let formattedURL = enumUrl?.replace("/api/", "");
      if (dependantValue) {
        formattedURL = `core/dynamic-options/provider-type/?provider=${dependantValue}`;
      }
      const results = await enumRepository.query({ urlPath: formattedURL });
      if (isMounted) {
        const newOptions = results ?? [];
        setOptions(newOptions);
      }
    };

    fetchOptions();

    return () => {
      isMounted = false;
    };
  }, [dependantValue, field.enum, enumRepository, value, handleChange, rowIdx]);

  const selectedOption = options.find((opt) => opt.key === value);
  const fallbackOption = selectedOption || (value ? { key: value, value: value } : null);

  return (
    <Autocomplete
      size="small"
      options={options}
      getOptionLabel={(option) => option?.value?.toString?.() || option?.label?.toString?.() || ""}
      value={fallbackOption}
      onChange={(_, newValue) => {
        handleChange(rowIdx, "provider_type", newValue?.key || "");
      }}
      readOnly={!isEditable}
      disableClearable
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          fullWidth
          placeholder="Select..."
          sx={sharedStyles.readOnlyField}
        />
      )}
      isOptionEqualToValue={(option, val) => option.key === val.key}
      popupIcon={isEditable ? <CustomArrowIcon /> : null}
    />
  );
};

const DynamicTable = ({
  addButtonText,
  columns = [],
  editableFields = [],
  label,
  schema,
  setEnableSave,
  setTableData,
  showAddButton,
  tableData = [],
  isReferrals = false,
  isAppointments = false,
}) => {
  const enumRepository = useService("repositories/enum-repository");
  const allowFullEdition = editableFields.includes("all");
  const [enumOptions, setEnumOptions] = useState({});
  const [dateErrors, setDateErrors] = useState({});
  const expectedInput = useMemo(() => schema?.expected_input?.items?.$ref, [schema]);
  const selectedSchema = useMemo(
    () => (expectedInput ? schema[expectedInput] : {}),
    [expectedInput, schema]
  );
  const fieldProps = useMemo(() => selectedSchema?.properties ?? {}, [selectedSchema]);
  const columnWidth = `${(100 - 5) / columns.length}%`;
  const { min: minDate, max: maxDate } = useMemo(() => getCenturyDateBounds(), []);

  const updateDateError = useCallback((rowIdx, key, hasError) => {
    setDateErrors((prev) => {
      const errorKey = `${rowIdx}-${key}`;
      if (hasError) {
        if (prev[errorKey]) {
          return prev;
        }
        return { ...prev, [errorKey]: true };
      }

      if (!prev[errorKey]) {
        return prev;
      }

      const { [errorKey]: _omit, ...rest } = prev;
      return rest;
    });
  }, []);

  // Fetch enum options only once per expectedInput
  useEffect(() => {
    if (!fieldProps) {
      return;
    }

    const fetchEnums = async () => {
      const entries = Object.entries(fieldProps).filter(([_, value]) => value.enum);
      const result = {};

      for (const [key, fieldSchema] of entries) {
        try {
          const enumUrl = fieldSchema.enum;
          const formattedURL = enumUrl?.replace("/api/", "");
          const results = await enumRepository.query({ urlPath: formattedURL });
          result[key] = results ?? [];
        } catch (err) {
          console.error(`Error fetching enum for ${key}:`, err);
        }
      }
      setEnumOptions(result);
    };

    fetchEnums();
  }, [fieldProps, enumRepository]);

  // Check if all required fields are completed
  useEffect(() => {
    const requiredFields = schema[expectedInput]?.required ?? [];
    const hasIncompleteRows = tableData.some((row) =>
      requiredFields.some((field) => !(field in row) || row[field] === "" || row[field] === null)
    );
    setEnableSave(!hasIncompleteRows);
  }, [tableData, schema, expectedInput, setEnableSave]);

  const handleChange = useCallback(
    (rowIdx, key, newValue) => {
      setTableData((prev) =>
        prev.map((item, idx) =>
          idx === rowIdx
            ? {
                ...item,
                [key]: newValue,
                ...(key === "referral_to" && { provider_type: "" }),
              }
            : item
        )
      );
    },
    [setTableData]
  );

  const handleRemoveRow = useCallback(
    (rowIdx) => {
      setTableData((prev) => prev.filter((_, idx) => idx !== rowIdx));
    },
    [setTableData]
  );

  const renderCellContent = useCallback(
    (key, value, rowIdx, isEditable) => {
      const field = fieldProps[key] || {};
      const options = enumOptions[key] || [];
      const format = field?.format;

      if (key === "provider_type") {
        const dependantValue = isReferrals
          ? tableData?.[rowIdx]?.["referral_to"]
          : isAppointments
            ? tableData?.[rowIdx]?.["provider"]
            : "";

        return (
          <ProviderTypeCell
            value={value}
            rowIdx={rowIdx}
            dependantValue={dependantValue}
            isEditable={isEditable}
            handleChange={handleChange}
            field={field}
          />
        );
      }

      if (!value && !isEditable) {
        return "";
      }

      if (options.length > 0) {
        const selectedOption = options.find((opt) => opt.key === value);
        const fallbackOption = selectedOption || (value ? { key: value, value: value } : null);
        const uniqueOptions = options.map((opt, idx) => ({
          ...opt,
          _uniqueId: `${opt?.key || opt?.value || "opt"}-${idx}`,
        }));

        return (
          <Autocomplete
            disableClearable
            filterOptions={(opts, state) =>
              opts.filter((opt) => {
                const label = (opt?.value || opt?.label || "").toString().toLowerCase();
                const input = state.inputValue.toLowerCase();
                return label.includes(input);
              })
            }
            getOptionLabel={(option) =>
              option?.value?.toString?.() || option?.label?.toString?.() || ""
            }
            isOptionEqualToValue={(option, val) => option?.key === val?.key}
            onChange={(_, newValue) => {
              handleChange(rowIdx, key, newValue?.key || "");
            }}
            options={uniqueOptions}
            popupIcon={isEditable ? <CustomArrowIcon /> : null}
            readOnly={!isEditable}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                fullWidth
                placeholder="Select..."
                sx={sharedStyles.readOnlyField}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option._uniqueId}>
                {option?.value?.toString?.() || option?.label?.toString?.()}
              </li>
            )}
            size="small"
            value={fallbackOption}
          />
        );
      }

      if (format === "date" || format === "date-time") {
        const errorKey = `${rowIdx}-${key}`;
        const parsedValue = toValidDate(value);
        const storedInvalid =
          Boolean(value) && (!parsedValue || !isWithinDateBounds(parsedValue, minDate, maxDate));
        const hasError = (isEditable && Boolean(dateErrors[errorKey])) || storedInvalid;
        const Picker = DatePicker;
        return isEditable ? (
          <Picker
            onChange={(newValue) => {
              const shouldFlagError =
                Boolean(newValue) &&
                (!isValidDate(newValue) || !isWithinDateBounds(newValue, minDate, maxDate));
              updateDateError(rowIdx, key, shouldFlagError);

              handleChange(
                rowIdx,
                key,
                newValue && isValidDate(newValue) && isWithinDateBounds(newValue, minDate, maxDate)
                  ? dfFormat(newValue, "yyyy-MM-dd")
                  : ""
              );
            }}
            value={toValidDate(value)}
            minDate={minDate}
            maxDate={maxDate}
            onError={(reason, newValue) => {
              const isInvalidFromReason = Boolean(reason);
              const isInvalidFromValue =
                Boolean(newValue) &&
                (!isValidDate(newValue) || !isWithinDateBounds(newValue, minDate, maxDate));

              updateDateError(rowIdx, key, isInvalidFromReason || isInvalidFromValue);
            }}
            slots={{
              openPickerIcon: () => (
                <Svg color={HNSColors.primary[500]} name="his-calendar" sx={{ margin: "0 1rem" }} />
              ),
            }}
            slotProps={{
              openPickerButton: {
                sx: { "&:hover": { background: "transparent" } },
              },
              textField: {
                fullWidth: true,
                variant: "standard",
                error: hasError,
                sx: {
                  ...sharedStyles.readOnlyField,
                  ...(hasError && {
                    border: "1px solid",
                    borderColor: "error.main",
                    borderRadius: "4px",
                  }),
                },
                inputProps: { "aria-invalid": hasError },
              },
            }}
          />
        ) : (
          <Typography variant="body2">{value ? formatDate(value, "MM/dd/yyyy") : ""}</Typography>
        );
      }

      if (format === "time") {
        return isEditable ? (
          <TimePicker
            value={value ? parseTimeStringToDate(value) : null}
            onChange={(newValue) => {
              handleChange(rowIdx, key, newValue ? toHHmmss(newValue) : "");
            }}
            slotProps={{
              openPickerButton: {
                sx: { "&:hover": { background: "transparent" } },
              },
              textField: {
                fullWidth: true,
                sx: sharedStyles.readOnlyField,
                variant: "standard",
              },
            }}
          />
        ) : (
          <Typography variant="body2">
            {value
              ? parseTimeStringToDate(value).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""}
          </Typography>
        );
      }

      if (Array.isArray(value)) {
        return value.join(", ");
      }

      if (formatIsoOrTime(value)) {
        return formatIsoOrTime(value);
      }

      return value || "";
    },
    [
      dateErrors,
      enumOptions,
      fieldProps,
      handleChange,
      tableData,
      minDate,
      maxDate,
      updateDateError,
    ]
  );

  return (
    <>
      <QuestionTitle label={label}>
        <>
          {showAddButton && (
            <Button
              className="w-52 rounded-3xl bg-white py-2 pl-8 pr-2 text-xs normal-case"
              onClick={() => handleAddRow(columns, fieldProps, setTableData)}
              sx={{
                color: HNSColors.secondary[500],
                border: `1px solid ${HNSColors.secondary[500]}`,
                "&:hover": {
                  background: HNSColors.secondary[900],
                  border: `1px solid ${HNSColors.secondary[500]}`,
                },
              }}
              variant="outlined"
            >
              {addButtonText}
              <AddCircleOutline fontSize="small" className="ml-auto" />
            </Button>
          )}
        </>
      </QuestionTitle>

      <TableContainer sx={{ my: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((key) => (
                <TableCell
                  key={key}
                  sx={{
                    fontWeight: "bold",
                    textTransform: "capitalize",
                    width: columnWidth,
                  }}
                >
                  {isAppointments && key === "provider"
                    ? "Provider Name"
                    : isAppointments && key === "scheduled"
                      ? "Date & Time"
                      : String(key).replaceAll("_", " ")}
                </TableCell>
              ))}
              {allowFullEdition && tableData.length > 0 && (
                <TableCell sx={{ fontWeight: "bold", width: "5%" }}></TableCell>
              )}
            </TableRow>
          </TableHead>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TableBody>
              {tableData.length > 0 ? (
                tableData.map((row, rowIdx) => (
                  <TableRow key={row?.id !== null ? row.id : rowIdx}>
                    {columns.map((key) => {
                      const value = row[key];
                      const isEditable = allowFullEdition || editableFields.includes(key);
                      return (
                        <TableCell className="py-4" key={key}>
                          {renderCellContent(key, value, rowIdx, isEditable)}
                        </TableCell>
                      );
                    })}
                    {allowFullEdition && (
                      <TableCell>
                        <IconButton
                          aria-label="delete"
                          onClick={() => handleRemoveRow(rowIdx)}
                          size="small"
                          style={{ color: HNSColors.failure[500] }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length + 1}>
                    <Typography className="w-full p-8 text-center">No data available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </LocalizationProvider>
        </Table>
      </TableContainer>
    </>
  );
};

export default DynamicTable;
